import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../api/api';

const auth = () => {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

export default function MassTransferModal({
  open,
  onClose,
  onSaved,
  employees = [],
}) {
  const [fromManagerId, setFromManagerId] = useState('');
  const [toManagerId, setToManagerId] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setFromManagerId('');
    setToManagerId('');
    setSelected(new Set());
    setError(null);
  }, [open]);

  // Anyone with at least one direct report is offered as a "from" candidate.
  const managers = useMemo(() => {
    const reports = new Map();
    employees.forEach((e) => {
      if (e.primary_manager_id) {
        reports.set(e.primary_manager_id, (reports.get(e.primary_manager_id) || 0) + 1);
      }
    });
    return employees.filter((e) => reports.has(e.id));
  }, [employees]);

  // The reports list under the selected "from" manager.
  const reports = useMemo(() => {
    if (!fromManagerId) return [];
    return employees.filter(
      (e) => String(e.primary_manager_id) === String(fromManagerId)
    );
  }, [employees, fromManagerId]);

  // Reset selection when "from" changes.
  useEffect(() => {
    setSelected(new Set());
  }, [fromManagerId]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const allSelected = reports.length > 0 && selected.size === reports.length;
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(reports.map((r) => r.id)));
  };

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!toManagerId) return setError('Pick a destination manager.');
    if (!selected.size) return setError('Select at least one employee.');
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/org-chart/mass-transfer`,
        {
          employee_ids: Array.from(selected),
          primary_manager_id: Number(toManagerId),
        },
        auth()
      );
      onSaved?.();
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.clear();
        window.location.assign('/login');
        return;
      }
      setError(err?.response?.data?.error || err.message || 'Transfer failed');
    } finally {
      setSaving(false);
    }
  };

  const toOptions = employees.filter(
    (e) => String(e.id) !== String(fromManagerId) && !selected.has(e.id)
  );

  return (
    <div className="org-modal-backdrop" onClick={onClose}>
      <div className="org-modal" onClick={(e) => e.stopPropagation()}>
        <h4>Mass Transfer</h4>
        <p className="org-modal-sub">
          Move some or all of one manager's reports to another manager.
        </p>

        {error && <div className="org-modal-error">{error}</div>}

        <form onSubmit={submit}>
          <div className="form-row">
            <label>From Manager</label>
            <select
              value={fromManagerId}
              onChange={(e) => setFromManagerId(e.target.value)}
              required
            >
              <option value="">Select source manager…</option>
              {managers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          {fromManagerId && (
            <div className="form-row">
              <label>
                Reports {reports.length > 0 ? `(${selected.size}/${reports.length})` : ''}
                {reports.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleAll}
                    style={{
                      float: 'right',
                      border: 0,
                      background: 'transparent',
                      color: '#f97316',
                      fontSize: 12,
                      cursor: 'pointer',
                      textTransform: 'none',
                      letterSpacing: 0,
                    }}
                  >
                    {allSelected ? 'Clear' : 'Select all'}
                  </button>
                )}
              </label>
              <div className="org-multiselect">
                {reports.length === 0 && (
                  <div style={{ padding: 8, color: '#94a3b8', fontSize: 12 }}>
                    This manager has no direct reports.
                  </div>
                )}
                {reports.map((r) => (
                  <label key={r.id} className="org-multiselect-row">
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggle(r.id)}
                    />
                    <span>
                      {r.name}
                      {r.designation ? ` · ${r.designation}` : ''}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="form-row">
            <label>To Manager</label>
            <select
              value={toManagerId}
              onChange={(e) => setToManagerId(e.target.value)}
              required
            >
              <option value="">Select destination manager…</option>
              {toOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} {m.designation ? `· ${m.designation}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="org-modal-foot">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Transferring…' : `Transfer ${selected.size || ''}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
